import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import type { KeyboardInfo } from '@capacitor/keyboard';

/**
 * Applica un inset inferiore dinamico basato sull'altezza della tastiera.
 * Aggiorna padding e scroll padding dell'elemento referenziato
 * sommando l'altezza della tastiera a un margine extra.
 */
export const useKeyboardInset = (
  ref: React.RefObject<HTMLElement>,
  extraBottom: number = 0
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const originalPadding = el.style.paddingBottom;
    const originalScrollPadding = el.style.scrollPaddingBottom;
    const originalOverflow = el.style.overflowY;

    const applyInset = (height: number) => {
      const total = height + extraBottom;
      const value = `${total}px`;
      el.style.paddingBottom = value;
      el.style.scrollPaddingBottom = value;
      el.style.overflowY = 'auto';
    };

    applyInset(0);

    if (!Capacitor.isPluginAvailable('Keyboard')) {
      return () => {
        el.style.paddingBottom = originalPadding;
        el.style.scrollPaddingBottom = originalScrollPadding;
        el.style.overflowY = originalOverflow;
      };
    }

    const showListener = Keyboard.addListener(
      'keyboardDidShow',
      (info: KeyboardInfo) => applyInset(info.keyboardHeight)
    );

    const hideListener = Keyboard.addListener('keyboardDidHide', () => applyInset(0));

    return () => {
      showListener.then(l => l.remove());
      hideListener.then(l => l.remove());
      el.style.paddingBottom = originalPadding;
      el.style.scrollPaddingBottom = originalScrollPadding;
      el.style.overflowY = originalOverflow;
    };
  }, [ref, extraBottom]);
};

